import Link from 'next/link';
import { Fragment } from 'react';

import type { NavBar } from '@/api/types/navigationTypes';
import { Icon } from '@/components/Icon/Icon';
import { type ButtonVariant } from '@/components/UI/Button/variant';
import { useIsScrolled } from '@/hooks/useIsScrolled';
import { cn } from '@/utils/ui';

import { type AccountNavigationLink, HeaderAccountNav } from './HeaderAccountNav';
import { HeaderCTAButtons } from './HeaderCTAButtons';
import { HeaderNav } from './HeaderNav';

export interface NavigationLink {
  title: React.ReactNode;
  variant: ButtonVariant;
  className?: string;
  onClick: () => Promise<void>;
}

export interface BaseHeaderProps {
  accountNavigationLinks?: AccountNavigationLink[];
  color?: 'white' | 'secondary';
  isSearchActive?: boolean;
  subNavBar?: NavBar;
}

interface HeaderProps extends BaseHeaderProps {
  brandIcon?: string;
  children?: React.ReactNode;
  className?: string;
  ctas?: NavigationLink[];
  isCentered?: boolean;
  isFixed?: boolean;
  isLogoLinkDisabled?: boolean;
  isScrollTransition?: boolean;
  logo?: React.ReactNode;
  logoLink?: string;
}

export function Header(props: HeaderProps): React.ReactNode {
  const {
    accountNavigationLinks,
    brandIcon,
    children = null,
    className = '',
    color = 'secondary',
    ctas,
    isCentered = false,
    isFixed = false,
    isScrollTransition = true,
    isLogoLinkDisabled = false,
    isSearchActive = false,
    logo,
    logoLink = '/',
    subNavBar,
  } = props;

  const isScrolled = useIsScrolled({ scrollPosition: 64 });

  const LinkComponent = isLogoLinkDisabled ? Fragment : Link;

  return (
    <header
      className={cn(
        'relative z-50 h-[64px] w-full bg-base-black md:h-[70px]',
        {
          'sticky top-0': isFixed,
          'flex justify-center': isCentered,
          'bg-white': color === 'white',
          scrolled: color !== 'white' && isScrolled && isScrollTransition,
        },
        className,
      )}
    >
      <div className="container h-[64px] max-w-full md:h-[70px] lg:mx-auto lg:max-w-coreMaxWidth">
        <div
          className={cn('relative mx-auto flex size-full items-center justify-between', {
            '!justify-center': isCentered,
          })}
        >
          {children ? (
            <HeaderNav variant="mobile" brandIcon={brandIcon}>
              {children}
            </HeaderNav>
          ) : null}

          <LinkComponent
            data-analytics="header-nav-root-link"
            className={cn(
              '!h-[64px] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 md:!h-[70px] md:w-[144px]',
              // Give explicit dimensions when Icon is absolutely positioned for a11y
              !isCentered &&
                'absolute left-1/2 top-1/2 flex size-[90px] -translate-x-1/2 -translate-y-1/2 items-center justify-center md:relative md:left-auto md:top-auto md:size-auto md:translate-x-0 md:translate-y-0',
            )}
            href={logoLink}
          >
            {logo ?? (
              <Icon
                alt="Fox"
                className={
                  isCentered
                    ? 'size-[90px] md:size-[137px]'
                    : 'absolute inset-0 m-auto size-[90px] sm:relative sm:inset-auto sm:m-0'
                }
                id={brandIcon ?? 'brand-fox-one'}
              />
            )}
          </LinkComponent>

          {children ? (
            <HeaderNav variant="desktop" brandIcon={brandIcon}>
              {children}
            </HeaderNav>
          ) : null}

          {!isCentered && ctas?.length ? <HeaderCTAButtons isSearchActive={isSearchActive} ctas={ctas} /> : null}
          <HeaderAccountNav
            isSearchActive={isSearchActive}
            accountNavigationLinks={accountNavigationLinks}
            color={color}
            subNavBar={subNavBar}
          />
        </div>
      </div>
    </header>
  );
}
